import React from 'react';
import { subDays, startOfDay, endOfDay, differenceInDays } from 'date-fns';
import { DateTimePicker } from '@/shared/components/ui/DateTimePicker';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface DateRangeFilterProps {
  startDate: Date;
  endDate: Date;
  onChange: (range: DateRange) => void;
  maxDays?: number;
  label?: string;
  className?: string;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  onChange,
  maxDays = 30,
  label = 'Date Range',
  className = '',
}) => {
  const daysDifference = differenceInDays(endDate, startDate);
  const isMaxRange = daysDifference >= maxDays;

  const quickRanges = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 14 days', days: 14 },
    { label: 'Last 30 days', days: 30 },
  ];

  const handleQuickRange = (days: number) => {
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, days));
    onChange({ startDate: start, endDate: end });
  };


  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {/* Quick Range Buttons */}
      <div className="flex gap-2 mb-3">
        {quickRanges.map((range) => (
          <button
            key={range.days}
            onClick={() => handleQuickRange(range.days)}
            className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200/50 bg-white text-gray-700 hover:bg-gray-50 hover:border-primary-300 transition-colors"
          >
            {range.label}
          </button>
        ))}
      </div>

      {/* Date Time Pickers */}
      <div className="grid grid-cols-2 gap-3">
        <DateTimePicker
          value={startDate}
          onChange={(date) => {
            const diff = differenceInDays(endDate, date);
            if (diff > maxDays) {
              onChange({
                startDate: date,
                endDate: endOfDay(subDays(endDate, diff - maxDays))
              });
            } else {
              onChange({ startDate: date, endDate });
            }
          }}
          label="From"
          maxDate={endDate}
        />
        <DateTimePicker
          value={endDate}
          onChange={(date) => {
            const diff = differenceInDays(date, startDate);
            if (diff > maxDays) {
              onChange({
                startDate: startOfDay(subDays(startDate, diff - maxDays)),
                endDate: date
              });
            } else {
              onChange({ startDate, endDate: date });
            }
          }}
          label="To"
          minDate={startDate}
          maxDate={new Date()}
          showTime
        />
      </div>

      {/* Warning if max range */}
      {isMaxRange && (
        <p className="mt-2 text-xs text-amber-600 flex items-center gap-1">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Maximum range of {maxDays} days selected
        </p>
      )}
    </div>
  );
};
